/**
 * Boss cast-bar / nameplate warning text (pure model; the HUD draws it).
 *
 * `castWarning()` turns an enemy cast into a short call-out: the ability's authored
 * `EnemyAbility.warning`, else a counterplay hint for dangerous casts ("Kick!", "Spread!").
 * `WarningFeed` is the boss-frame banner state machine: it shows the most severe warning,
 * holds it long enough to read (≥ MIN_SHOW_MS, through the cast) and never flickers between
 * two simultaneous casts.
 */
import type { CounterHint, ContentRegistry, EnemyAbility } from '@keystone/sim';

export type WarningSeverity = 'info' | 'danger' | 'deadly';

export interface CastWarning {
  text: string;
  severity: WarningSeverity;
  /** Short counterplay tag shown next to the cast bar ("KICK", "DODGE"...), if any. */
  hint: string | null;
  /** CSS colour for the text (warm, moderate; deadly is red, danger orange, info pale amber). */
  color: string;
}

export const WARNING_COLOR: Record<WarningSeverity, string> = {
  info: '#e8cf9a',
  danger: '#f0903a',
  deadly: '#e8473a',
};

const HINT_TEXT: Partial<Record<CounterHint, string>> = {
  interrupt: 'KICK',
  dodge: 'DODGE',
  spread: 'SPREAD',
  soak: 'SOAK',
  stack: 'STACK',
  lookAway: 'LOOK AWAY',
  behindCaster: 'GET BEHIND',
  kite: 'KITE',
  burstShield: 'BREAK SHIELD',
  defensive: 'DEFENSIVE',
  healerCd: 'HEAL CD',
  dispel: 'DISPEL',
  runOut: 'RUN OUT',
  tankFacing: 'FACE AWAY',
  killAdd: 'KILL ADD',
};

/** Fallback sentences for unauthored dangerous casts, by first counter hint. */
const HINT_SENTENCE: Partial<Record<CounterHint, string>> = {
  interrupt: 'Interrupt!',
  spread: 'Spread out!',
  soak: 'Soak it!',
  stack: 'Stack up!',
  lookAway: 'Look away!',
  behindCaster: 'Get behind!',
  runOut: 'Run out!',
  burstShield: 'Break the shield!',
};

/** First hint that is a player action (used for the short tag). */
export function primaryHint(counter: readonly CounterHint[] | undefined): string | null {
  if (!counter) return null;
  for (const c of counter) {
    const t = HINT_TEXT[c];
    if (t) return t;
  }
  return null;
}

/**
 * Warning for an enemy cast, or null when the cast deserves none (plain bolts, fillers).
 * `bossCaster`: casts by bosses always get at least an info line with the hint.
 */
export function castWarning(ab: Pick<EnemyAbility, 'name' | 'warning' | 'counter' | 'kind'> | undefined, bossCaster = false): CastWarning | null {
  if (!ab) return null;
  const hint = primaryHint(ab.counter);
  if (ab.warning) return { text: ab.warning.text, severity: ab.warning.severity, hint, color: WARNING_COLOR[ab.warning.severity] };
  const first = ab.counter?.[0];
  const dangerous = ab.kind === 'tankbuster' || ab.kind === 'pulse' || ab.kind === 'frontal' || ab.kind === 'groundAoE' || ab.kind === 'cc' || ab.kind === 'heal' || ab.kind === 'shield';
  if (first && dangerous) {
    const s = HINT_SENTENCE[first];
    if (s) return { text: s, severity: 'danger', hint, color: WARNING_COLOR.danger };
  }
  if (bossCaster && hint) return { text: ab.name, severity: 'info', hint, color: WARNING_COLOR.info };
  return null;
}

/**
 * Raid-warning banner line: "Ritual of the Fang — SOAK!" (name + counterplay tag), or the name
 * alone. `sub` is the authored sentence when it adds something beyond the tag.
 */
export function bannerText(name: string, w: CastWarning): { main: string; sub: string | null } {
  const main = w.hint ? `${name} \u2014 ${w.hint}!` : name;
  const t = w.text.trim();
  const same = t.toLowerCase() === name.toLowerCase() || (!!w.hint && t.replace(/[!.]/g, '').toLowerCase() === w.hint.toLowerCase());
  return { main, sub: same ? null : t };
}

/** Convenience lookup through the registry. */
export function castWarningFor(content: ContentRegistry, abilityId: string, bossCaster = false): CastWarning | null {
  return castWarning(content.enemyAbility(abilityId), bossCaster);
}

const RANK: Record<WarningSeverity, number> = { info: 0, danger: 1, deadly: 2 };

/** Minimum time a banner stays up so it can be read. */
export const MIN_SHOW_MS = 2500;

/**
 * Boss-frame warning banner. Feed it cast starts (`push`) and call `current(now)` each frame.
 * A new warning replaces the shown one only if it is at least as severe, or the shown one has
 * been up for MIN_SHOW_MS and its cast is over.
 */
export class WarningFeed {
  private shown: CastWarning | null = null;
  private shownAt = 0;
  private until = 0;
  private abilityId: string | null = null;

  push(w: CastWarning | null, abilityId: string, now: number, castEndsAt: number): void {
    if (!w) return;
    const end = Math.max(now + MIN_SHOW_MS, castEndsAt);
    if (this.shown && this.abilityId === abilityId) {
      this.until = Math.max(this.until, end);
      return;
    }
    const replace = !this.shown || now >= this.until || RANK[w.severity] >= RANK[this.shown.severity];
    if (!replace) return;
    this.shown = w;
    this.shownAt = now;
    this.until = end;
    this.abilityId = abilityId;
  }

  /** The warning to show now (null when none). */
  current(now: number): CastWarning | null {
    if (this.shown && now >= this.until) {
      this.shown = null;
      this.abilityId = null;
    }
    return this.shown;
  }

  /** Ability of the shown warning (null when none). */
  get shownAbilityId(): string | null {
    return this.shown ? this.abilityId : null;
  }

  /** 0..1 fade-in pulse for the first 300 ms (CSS can read it). */
  pulse(now: number): number {
    return this.shown ? Math.max(0, 1 - (now - this.shownAt) / 300) : 0;
  }

  clear(): void {
    this.shown = null;
    this.abilityId = null;
  }
}
